/**
 * The tour's one guard. While it is installed nothing in the product responds
 * unless the current scene allows it: every initiating event is judged on the
 * window, in the capture phase, before the product hears it.
 */

import {
  COVERS_ATTR,
  GUARD_ATTR,
  HOT_ATTR,
  RESIZE_SELECTOR,
  TOUR_UI_ATTR,
  chromeAt,
  chromeStyles,
} from "./chrome";

export type Zone =
  /** The tour's own surfaces: card, pill, fallback. */
  | "tour"
  /** A product control the scene lets through. */
  | "pass"
  /** The canvas: the director acts on it, the product does not hear it. */
  | "canvas"
  /** A product surface that may scroll and nothing else. */
  | "scroll"
  /** A divider that may be dragged, whatever the scene. */
  | "resize"
  | "other";

export type Verdict =
  | "pass"
  /** Stop it, quietly. */
  | "stop"
  /** Stop it and point at what to do. */
  | "nudge"
  /** Stop it and hand it to the director's canvas gestures. */
  | "canvas"
  /** Stop it and run the scene's Enter action. */
  | "enter";

export interface Judged {
  type: string;
  zone: Zone;
  /** The scene is live: its allowed actions respond. */
  open: boolean;
  /** An allowed press is still down (a drag out of the card). */
  gesture: boolean;
  key?: string;
  ctrl?: boolean;
  meta?: boolean;
  shift?: boolean;
  alt?: boolean;
  repeat?: boolean;
  /** `mouse`, `touch` or `pen` on pointer events. */
  pointerType?: string;
  button?: number;
}

const HOVER = new Set([
  "pointerover",
  "pointerout",
  "pointerenter",
  "pointerleave",
  "pointermove",
  "mouseover",
  "mouseout",
  "mouseenter",
  "mouseleave",
  "mousemove",
]);
const PRESS = new Set(["pointerdown", "mousedown"]);
const CLICKS = new Set(["click", "dblclick", "auxclick", "contextmenu"]);
const TOUCH = new Set(["touchstart", "touchmove", "touchend", "touchcancel"]);
const DRAG = new Set(["dragstart", "dragenter", "dragover", "drop"]);
const CLIPBOARD = new Set(["paste", "cut", "copy"]);
const KEYS = new Set(["keydown", "keypress", "keyup"]);

export const GUARDED_EVENTS: readonly string[] = [
  ...HOVER,
  ...PRESS,
  ...CLICKS,
  ...TOUCH,
  ...DRAG,
  ...CLIPBOARD,
  ...KEYS,
  "wheel",
];

const MODIFIERS = new Set(["Shift", "Control", "Alt", "Meta", "CapsLock"]);

/** Reload, zoom and devtools belong to the browser, not the product. */
function isBrowserKey(e: Judged): boolean {
  const key = e.key ?? "";
  if (key === "F5" || key === "F12") return true;
  if (!e.ctrl && !e.meta) return false;
  const k = key.toLowerCase();
  if (k === "r" || k === "+" || k === "-" || k === "=" || k === "0")
    return true;
  return !!(e.shift || e.alt) && (k === "i" || k === "j" || k === "c");
}

export function judge(e: Judged): Verdict {
  const { type, zone } = e;

  if (KEYS.has(type)) {
    if (isBrowserKey(e) || e.key === "Tab" || MODIFIERS.has(e.key ?? ""))
      return "pass";
    const plain = !e.ctrl && !e.meta && !e.alt;
    // The card's own buttons take Enter and Space themselves.
    if (zone === "tour" && plain && (e.key === "Enter" || e.key === " "))
      return "pass";
    if (type !== "keydown" || e.repeat) return "stop";
    if (e.key === "Enter" && plain && !e.shift)
      return e.open ? "enter" : "stop";
    return e.open ? "nudge" : "stop";
  }

  if (zone === "tour") return "pass";
  if (e.gesture && (HOVER.has(type) || TOUCH.has(type))) return "pass";
  if (zone === "resize")
    return HOVER.has(type) ||
      TOUCH.has(type) ||
      (PRESS.has(type) && !e.button) ||
      type === "click"
      ? "pass"
      : "stop";

  if (type === "wheel") return zone === "scroll" ? "pass" : "stop";
  if (TOUCH.has(type)) {
    if (zone === "scroll") return "pass";
    return zone === "pass" && e.open ? "pass" : "stop";
  }
  if (HOVER.has(type)) return zone === "pass" && e.open ? "pass" : "stop";
  if (DRAG.has(type) || CLIPBOARD.has(type)) return "stop";

  if (PRESS.has(type)) {
    if (zone === "pass" && e.open) return "pass";
    // A finger has to land for a scroll to start; the click after it is stopped.
    if (zone === "scroll" && e.pointerType === "touch") return "pass";
    if (zone === "canvas" && e.open && type === "pointerdown" && !e.button)
      return "canvas";
    return "stop";
  }

  if (CLICKS.has(type)) {
    if (zone === "pass" && e.open && type !== "contextmenu") return "pass";
    // Canvas clicks were already taken as gestures on the press.
    if (zone === "canvas" && e.open && type !== "contextmenu") return "stop";
    return e.open ? "nudge" : "stop";
  }

  return "stop";
}

export interface GuardConfig {
  /** Selectors the scene lets through to the product right now. */
  pass: readonly string[];
  open: boolean;
}

export interface GuardHandlers {
  config: () => GuardConfig;
  onNudge: () => void;
  onEnter: () => void;
  /** A click the product is about to hear, on a control the scene allows. */
  onPass: (target: Element) => void;
  /** A press on the canvas, then its moves and its release. */
  onCanvasDown: (e: PointerEvent) => void;
  onCanvasMove: (e: PointerEvent) => void;
  onCanvasUp: (e: PointerEvent) => void;
  /** Whether a press here, on the canvas, would do something. */
  canvasHot: (e: PointerEvent) => boolean;
  /** A divider was dragged and let go. */
  onResized: () => void;
  /** Any pointer went down or came up, wherever it was. */
  onPointer: (down: boolean) => void;
}

const CANVAS_SELECTOR = ".react-flow";
const CANVAS_CHROME_SELECTOR = '[role="toolbar"], .react-flow__panel';
/** Product surfaces that keep their scroll: the chat transcript, the deck. */
export const SCROLL_SELECTORS: readonly string[] = [
  '[data-floating-chat="window"]',
  ".js-right-pane-panel",
];
/** Same-origin frames the guard reaches into. */
const FRAME_SELECTOR = ".js-right-pane-panel iframe";
const STYLE_ID = "co-tour-guard-styles";
/** Presses this close together are one click's worth of nudging. */
const NUDGE_GAP_MS = 400;
const DRAG_PX = 6;
const AFTER_DRAG_MS = 350;
const REACH_MS = 500;

export function zoneOf(
  target: EventTarget | null,
  pass: readonly string[],
  /** Where a press or click landed on screen. */
  at?: { x: number; y: number },
): Zone {
  const el =
    target instanceof Element
      ? target
      : target instanceof Node
        ? target.parentElement
        : null;
  if (!el) return "other";
  if (el.closest(`[${TOUR_UI_ATTR}]`)) return "tour";
  if (el.closest(RESIZE_SELECTOR)) return "resize";
  for (const selector of pass) if (el.closest(selector)) return "pass";
  for (const selector of SCROLL_SELECTORS)
    if (el.closest(selector)) return "scroll";
  if (!el.closest(CANVAS_SELECTOR) || el.closest(CANVAS_CHROME_SELECTOR))
    return "other";
  // A press on chrome at rest lands on the canvas beneath it; by where it
  // is, it is still a press on chrome.
  return at && chromeAt(at.x, at.y) ? "other" : "canvas";
}

/** Install the guard; the returned function removes every trace of it. */
export function installGuard(handlers: GuardHandlers): () => void {
  let gesture = false;
  let downAt: { x: number; y: number } | null = null;
  let dragged = false;
  let onCanvas = false;
  let resizing = false;
  let swallowClicksUntil = 0;
  let lastNudge = 0;

  const nudge = () => {
    const now = Date.now();
    if (now - lastNudge < NUDGE_GAP_MS) return;
    lastNudge = now;
    handlers.onNudge();
  };

  const handle = (inFrame: boolean) => (event: Event) => {
    const { pass, open } = handlers.config();
    const e = event as Partial<KeyboardEvent & PointerEvent>;
    const type = event.type;
    const at =
      (PRESS.has(type) || CLICKS.has(type) || type === "pointermove") &&
      e.clientX !== undefined
        ? { x: e.clientX, y: e.clientY ?? 0 }
        : undefined;
    const zone = inFrame ? "scroll" : zoneOf(event.target, pass, at);

    if (type === "pointerdown") handlers.onPointer(true);
    if (type === "pointermove") {
      if (onCanvas) handlers.onCanvasMove(event as PointerEvent);
      else if (!gesture)
        document.documentElement.toggleAttribute(
          HOT_ATTR,
          zone === "canvas" &&
            open &&
            handlers.canvasHot(event as PointerEvent),
        );
    }

    if (
      type === "click" &&
      Date.now() < swallowClicksUntil &&
      zone !== "tour"
    ) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return;
    }

    const verdict = judge({
      type,
      zone,
      open,
      gesture,
      key: e.key,
      ctrl: e.ctrlKey,
      meta: e.metaKey,
      shift: e.shiftKey,
      alt: e.altKey,
      repeat: e.repeat,
      pointerType: e.pointerType,
      button: e.button,
    });

    if (verdict === "pass") {
      if (type === "pointerdown") {
        gesture = true;
        resizing = zone === "resize";
        dragged = false;
        downAt = { x: e.clientX ?? 0, y: e.clientY ?? 0 };
      } else if (type === "pointermove" && gesture && downAt) {
        if (
          Math.hypot((e.clientX ?? 0) - downAt.x, (e.clientY ?? 0) - downAt.y) >
          DRAG_PX
        )
          dragged = true;
      } else if (
        type === "click" &&
        zone === "pass" &&
        event.target instanceof Element
      ) {
        handlers.onPass(event.target);
      }
      return;
    }

    event.stopImmediatePropagation();
    // A stopped move or hover has no default worth cancelling.
    if (event.cancelable && !HOVER.has(type)) event.preventDefault();
    if (verdict === "nudge") nudge();
    else if (verdict === "enter") handlers.onEnter();
    else if (verdict === "canvas") {
      onCanvas = true;
      handlers.onCanvasDown(event as PointerEvent);
    }
  };

  // Releases are never stopped: a press the product did hear must end.
  const release = (event: Event) => {
    if (gesture && dragged) swallowClicksUntil = Date.now() + AFTER_DRAG_MS;
    gesture = false;
    downAt = null;
    if (onCanvas) {
      onCanvas = false;
      handlers.onCanvasUp(event as PointerEvent);
    }
    handlers.onPointer(false);
    if (resizing) {
      resizing = false;
      handlers.onResized();
    }
  };

  const options = { capture: true, passive: false } as const;
  const attach = (win: Window, inFrame: boolean) => {
    const listener = handle(inFrame);
    for (const type of GUARDED_EVENTS)
      win.addEventListener(type, listener, options);
    win.addEventListener("pointerup", release, true);
    win.addEventListener("pointercancel", release, true);
    return () => {
      for (const type of GUARDED_EVENTS)
        win.removeEventListener(type, listener, options);
      win.removeEventListener("pointerup", release, true);
      win.removeEventListener("pointercancel", release, true);
    };
  };
  const detachMain = attach(window, false);

  // The deck is a same-origin frame with its own window: same guard, where
  // only scrolling passes.
  const framed = new Map<Window, () => void>();
  const reach = () => {
    for (const frame of document.querySelectorAll<HTMLIFrameElement>(
      FRAME_SELECTOR,
    )) {
      let win: Window | null = null;
      try {
        win = frame.contentWindow;
        void win?.document;
      } catch {
        win = null;
      }
      if (!win || framed.has(win)) continue;
      framed.set(win, attach(win, true));
    }
  };
  const reachTimer = window.setInterval(reach, REACH_MS);
  reach();

  document.documentElement.setAttribute(GUARD_ATTR, "");
  let style = document.getElementById(STYLE_ID);
  if (!style) {
    style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = chromeStyles();
    document.head.appendChild(style);
  }

  return () => {
    detachMain();
    window.clearInterval(reachTimer);
    for (const detach of framed.values()) {
      try {
        detach();
      } catch {
        // The frame is gone, and its listeners with it.
      }
    }
    document.documentElement.removeAttribute(GUARD_ATTR);
    document.documentElement.removeAttribute(COVERS_ATTR);
    document.documentElement.removeAttribute(HOT_ATTR);
    document.getElementById(STYLE_ID)?.remove();
  };
}
